import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiGlobe, FiServer, FiShield, FiMail, FiFileText, FiUsers, FiLock } = FiIcons;

const CoreServices = () => {
  const services = [
    {
      icon: FiGlobe,
      title: "Domain Acquisition & Portfolio Management",
      description: "Strategic domain acquisition, portfolio optimization, and comprehensive management services for enterprise clients.",
      features: [
        "Strategic domain research",
        "Portfolio analysis",
        "Acquisition negotiation",
        "Ongoing management"
      ]
    },
    {
      icon: FiServer,
      title: "DNS & Failover Setup",
      description: "Enterprise-grade DNS configuration with redundancy and failover capabilities to ensure maximum uptime.",
      features: [
        "Global DNS deployment",
        "Automatic failover",
        "Load balancing",
        "Performance optimization"
      ]
    },
    {
      icon: FiShield,
      title: "Domain Recovery & Brand Protection",
      description: "Comprehensive brand protection services including domain monitoring, recovery, and legal support.",
      features: [
        "Brand monitoring",
        "Domain recovery",
        "Legal assistance",
        "Trademark protection"
      ]
    },
    {
      icon: FiMail,
      title: "Email Security Services",
      description: "Advanced email protection powered by Xcitium's cutting-edge security platform.",
      features: [
        "SPAM filtering",
        "Threat detection",
        "Zero-day protection",
        "Compliance reporting"
      ]
    },
    {
      icon: FiLock,
      title: "Cybersecurity Solutions",
      description: "Comprehensive cybersecurity services powered by Xcitium's advanced threat protection platform.",
      features: [
        "Endpoint protection",
        "Network security",
        "Threat intelligence",
        "Security monitoring"
      ]
    },
    {
      icon: FiFileText,
      title: "Infrastructure Audits & Compliance",
      description: "Comprehensive security audits and compliance assessments for your digital infrastructure.",
      features: [
        "Security audits",
        "Compliance checks",
        "Risk assessment",
        "Remediation planning"
      ]
    },
    {
      icon: FiUsers,
      title: "Strategic Brand Consulting",
      description: "Expert consulting services to help align your digital brand strategy with business objectives.",
      features: [
        "Brand strategy",
        "Digital presence audit",
        "Growth planning",
        "Executive consulting"
      ]
    }
  ];

  return (
    <section id="services" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">
            Core Services
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Comprehensive digital brand management and cybersecurity solutions tailored to enterprise needs.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="bg-slate-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-300 group border border-slate-200"
            >
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-emerald-600 transition-colors duration-300">
                <SafeIcon 
                  icon={service.icon} 
                  className="w-6 h-6 text-emerald-600 group-hover:text-white transition-colors duration-300" 
                />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-4">
                {service.title}
              </h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                {service.description}
              </p>
              <ul className="space-y-2">
                {service.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center text-sm text-slate-600">
                    <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full mr-3"></div>
                    {feature}
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CoreServices;